import React from "react";
import { Phone, Mail, MapPin } from "lucide-react";
import ContactForm from "../components/ContactForm";
import { SITE, LOCATIONS } from "../data/mock";
import { useLang } from "../context/LanguageContext";

const Contact = () => {
  const { t } = useLang();
  return (
    <main className="bg-white dark:bg-black transition-colors">
      <section className="bg-[#800000] py-16 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute -top-20 -right-20 w-80 h-80 bg-[#14532d] rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-white rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">{t("contact.title")}</h1>
          <p className="text-white/90 text-lg max-w-2xl mx-auto">{t("contact.subtitle")}</p>
          <div className="flex items-center justify-center gap-2 mt-5">
            <div className="h-1 w-12 bg-white rounded-full" />
            <div className="h-1 w-6 bg-[#14532d] rounded-full" />
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-6">{t("contact.info_title")}</h2>
              <ul className="space-y-4 mb-10">
                <li className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#800000]/10 dark:bg-[#800000]/30 text-[#800000] dark:text-[#b85a6f] flex items-center justify-center flex-shrink-0">
                    <Phone size={18} />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">{t("contact.phone_label")}</p>
                    <a href={`tel:${SITE.phone.replace(/[^0-9]/g, "")}`} className="text-slate-900 dark:text-white font-semibold hover:text-[#800000] dark:hover:text-[#b85a6f] transition-colors">{SITE.phone}</a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#14532d]/10 dark:bg-emerald-900/40 text-[#14532d] dark:text-emerald-400 flex items-center justify-center flex-shrink-0">
                    <Mail size={18} />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">{t("contact.email_label")}</p>
                    <a href={`mailto:${SITE.email}`} className="text-slate-900 dark:text-white font-semibold hover:text-[#800000] dark:hover:text-[#b85a6f] transition-colors break-all">{SITE.email}</a>
                  </div>
                </li>
              </ul>

              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">{t("contact.locations")}</h3>
              <ul className="space-y-3">
                {LOCATIONS.map((loc) => (
                  <li key={loc.id} className="flex items-start gap-3 p-4 bg-slate-50 dark:bg-zinc-900 rounded-md hover:bg-slate-100 dark:hover:bg-zinc-800 transition-colors">
                    <MapPin size={18} className="text-[#800000] dark:text-[#b85a6f] flex-shrink-0 mt-0.5" />
                    <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc.address)}`} target="_blank" rel="noreferrer" className="text-slate-700 dark:text-slate-200 hover:text-[#800000] dark:hover:text-[#b85a6f] transition-colors">
                      {loc.address}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="rounded-lg overflow-hidden shadow-lg border-4 border-[#14532d] dark:border-emerald-900 h-full min-h-[400px]">
                <iframe
                  title="Yoshitaka Locations Map"
                  src="https://www.google.com/maps/embed?pb=!1m16!1m12!1m3!1d47565.123!2d-87.7!3d41.85!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!2m1!1sYoshitaka%20Karate%20Chicago!5e0!3m2!1sen!2sus!4v1700000000000"
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: 400 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 dark:bg-zinc-950 py-16 md:py-20 transition-colors">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <ContactForm titleKey="form.register_title" variant="plain" formType="Register" />
        </div>
      </section>
    </main>
  );
};

export default Contact;
